/**
 * Classify a checkout error response from `/api/payments/order-id` into a known
 * FREE-ticket business error, so the UI shows a friendly message instead of a generic
 * server error. Pure + unit-tested; paid checkout never produces these.
 *
 * - FREE_TICKET_ALREADY_ISSUED: backend 409 — the person already holds a free ticket for
 *   this event (matched by email OR phone). Stable code from the backend.
 * - FREE_SINGLE_ADMISSION: defensive mapping of the backend 400 `number_invitees` rejection
 *   on a free ticket (the UI already locks free quantity to 1, so this should not happen).
 */
export type FreeTicketBusinessError = 'FREE_TICKET_ALREADY_ISSUED' | 'FREE_SINGLE_ADMISSION' | null;

export function classifyFreeTicketError(params: {
  errorCode?: string | null;
  message?: string | null;
  isFree?: boolean;
}): FreeTicketBusinessError {
  const { errorCode, message, isFree } = params;

  if (errorCode === 'FREE_TICKET_ALREADY_ISSUED') {
    return 'FREE_TICKET_ALREADY_ISSUED';
  }

  // Defensive: the backend caps a free emission at one admission. There is no stable code
  // for it, so fall back to text — but ONLY for free tickets, never touching paid checkout.
  if (isFree && typeof message === 'string' && /number_invitees/i.test(message)) {
    return 'FREE_SINGLE_ADMISSION';
  }

  return null;
}
